import React, { useMemo, useState } from 'react';
import {
  BookOpen,
  Layers3,
  MessageSquareText,
  BriefcaseBusiness,
  Sparkles,
  Loader2,
  AlertCircle,
} from 'lucide-react';

import { useTalentDiagnosticsDefinitions } from '../../hooks/useTalentDiagnostics';

import { TalentDiagnosticsSectionsTab } from './tabs/TalentDiagnosticsSectionsTab';
import { TalentDiagnosticsQuestionsTab } from './tabs/TalentDiagnosticsQuestionsTab';
import { TalentDiagnosticsBenchmarksTab } from './tabs/TalentDiagnosticsBenchmarksTab';
import { TalentDiagnosticsRecommendationsTab } from './tabs/TalentDiagnosticsRecommendationsTab';

const tabs = [
  { key: 'sections', label: 'Sections', icon: Layers3 },
  { key: 'questions', label: 'Questions', icon: MessageSquareText },
  { key: 'benchmarks', label: 'Benchmarks', icon: BriefcaseBusiness },
  { key: 'recommendations', label: 'Recommendations', icon: Sparkles },
] as const;

type TabKey = (typeof tabs)[number]['key'];

export const TalentDiagnosticsConfig: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('sections');
  const [selectedDefinitionId, setSelectedDefinitionId] = useState<string>('');

  const {
    data: definitions = [],
    isLoading: definitionsLoading,
    isError: definitionsError,
  } = useTalentDiagnosticsDefinitions();

  const selectedDefinition = useMemo(
    () =>
      definitions.find(
        (definition) => definition.id === selectedDefinitionId
      ) ?? null,
    [definitions, selectedDefinitionId]
  );

  return (
    <div className="w-full animate-fade-in space-y-6">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">

        <div className="border-b border-slate-100 bg-slate-50/30 p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">

            <div className="flex flex-col gap-1 text-left">
              <h3 className="text-lg font-bold text-slate-900">
                Diagnostics Configuration
              </h3>

              <p className="text-sm text-slate-500">
                Choose a diagnostic definition to manage its content.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <BookOpen
                  size={16}
                  className="text-indigo-600"
                />

                Diagnostic Definition
              </label>

              <select
                value={selectedDefinitionId}
                onChange={(e) =>
                  setSelectedDefinitionId(e.target.value)
                }
                className="min-w-[16rem] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
              >
                <option value="">
                  Select a definition
                </option>

                {definitions.map((definition) => (
                  <option
                    key={definition.id}
                    value={definition.id ?? ''}
                  >
                    {definition.name || 'Untitled definition'}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="p-6">

          {definitionsError && (
            <div className="mb-6 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              <AlertCircle
                size={18}
                className="mt-0.5 shrink-0"
              />

              <div>
                <p className="font-semibold">
                  Failed to load diagnostic definitions.
                </p>

                <p className="mt-1">
                  Please refresh and try again.
                </p>
              </div>
            </div>
          )}

          <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            {/* <p className="text-sm font-semibold text-slate-600">
              Selected Definition
            </p> */}

            <p className="mt-1 text-lg font-semibold text-slate-900">
              {selectedDefinition?.name ??
                'Select a diagnostic definition'}
            </p>

            <p className="mt-1 text-sm text-slate-500">
              {selectedDefinition?.description ??
                'Choose a definition to begin managing content.'}
            </p>
          </div>

          {!selectedDefinitionId ? (
            <div className="mb-6 rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 p-8 text-center text-sm text-slate-500">
              Select a diagnostic definition to continue.
            </div>
          ) : (
            <>
              <div className="mb-6 flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const active = activeTab === tab.key;

                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setActiveTab(tab.key)}
                      className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${
                        active
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-slate-600 hover:bg-white hover:text-slate-900'
                      }`}
                    >
                      <Icon size={16} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {definitionsLoading ? (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-slate-50/70 py-12 text-slate-400">
                  <Loader2 className="mb-2 h-8 w-8 animate-spin" />
                  <p>Loading...</p>
                </div>
              ) : (
                <div>
                  {activeTab === 'sections' && (
                    <TalentDiagnosticsSectionsTab
                      definitionId={selectedDefinitionId}
                    />
                  )}

                  {activeTab === 'questions' && (
                    <TalentDiagnosticsQuestionsTab
                      definitionId={selectedDefinitionId}
                    />
                  )}

                  {activeTab === 'benchmarks' && (
                    <TalentDiagnosticsBenchmarksTab
                      definitionId={selectedDefinitionId}
                    />
                  )}

                  {activeTab === 'recommendations' && (
                    <TalentDiagnosticsRecommendationsTab
                      definitionId={selectedDefinitionId}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};